/**
 * /comparisons/obsidian — "Draftlize vs Obsidian for AI product work"
 * comparison page. Targets the "obsidian alternative" /
 * "obsidian for product management" keyword cluster.
 */
import { MarketingShell } from '../MarketingShell';

export function Obsidian() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/comparisons/obsidian"
      documentTitle="Obsidian Alternative for AI Product Work: Draftlize vs Obsidian · Draftlize"
      documentDescription="Looking for an Obsidian alternative for product decisions? Obsidian is the best local note vault for a human reader. Draftlize is a decision substrate built for AI agents to read and write — with dependency tracking that flags what goes stale when you change your mind."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Comparison · vs Obsidian</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}
          >
            Obsidian is the best place to keep notes —<br/>
            <em>for a human to read later.</em>
          </h1>
          <p className="lede">
            Obsidian is a beautiful local vault: plain markdown, backlinks, a graph that rewards you when future-you comes back to read. But product decisions have a different primary reader now — an AI agent in Claude Code or Cursor — and a different demand: change one decision, and everything that depended on it should know. Markdown notes can't do that. They're static prose a person reads; there's no dependency tracking, and an agent can't write back into them with structure. Draftlize isn't a better note app. It's a different shape for a different job.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Obsidian stores notes for <em>you.</em> Draftlize stores decisions for <em>agents.</em></h2>
          </div>
          <p>Obsidian is a superb personal knowledge base — no argument there. But a knowledge base is built around a human reading prose later, and that's exactly the wrong shape when the reader is an AI agent and the content is a live web of product decisions that depend on each other.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Obsidian</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>The unit you persist</td><td className="ours yes">Structured decision / spec / PRD cards</td><td className="partial">Free-form markdown notes</td></tr>
            <tr><td>Change one thing, what it breaks gets flagged</td><td className="ours yes">Dependency graph + stale marks</td><td className="no">Backlinks point, but nothing goes stale</td></tr>
            <tr><td>An AI agent can write back, with structure</td><td className="ours yes">Agents create &amp; update cards via MCP</td><td className="no">Agents can edit text, but it's just prose</td></tr>
            <tr><td>Self-contradictions surface on their own</td><td className="ours yes">Conflict detection across discussions</td><td className="no">Never cross-checks your notes</td></tr>
            <tr><td>Every decision has a fixed address to cite</td><td className="ours yes">Cards are addressable (URL, ID)</td><td className="partial">A note path, but no decision identity</td></tr>
            <tr><td>Built for an AI agent as the primary reader</td><td className="ours yes">The card graph IS agent context</td><td className="no">Built for a human reader</td></tr>
            <tr><td>Local-first, offline, your files on disk</td><td className="no">Hosted substrate</td><td className="yes">Local markdown you own</td></tr>
            <tr><td>Plugin ecosystem &amp; personal PKM</td><td className="partial">not the goal</td><td className="yes">Its home turf</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Where <em>Obsidian wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Local-first &amp; private</h3>
            <p>Your vault is plain markdown files on your own disk. No server, works offline, and nothing leaves your machine unless you choose. For a personal knowledge base you want to keep for a decade, that ownership is the right default — and Draftlize, as a hosted substrate, doesn't match it.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Plugin ecosystem &amp; flexibility</h3>
            <p>Daily notes, canvas, Dataview, hundreds of community plugins — Obsidian bends to almost any personal workflow. Draftlize is deliberately narrow: one opinionated shape for product decisions, not a blank canvas you mold.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Thinking for future-you to read</h3>
            <p>Reading notes, research, journals, a Zettelkasten — anything where the reader is a person revisiting their own thoughts, Obsidian is excellent. Draftlize is for when the primary reader is an AI agent, not a future human, and that changes everything about the format.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Where <em>Draftlize wins.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Change one decision, the rest follows</h3>
            <p>Reverse a pricing call upstream and every spec, PRD, and downstream decision that leaned on it gets marked stale — automatically. Obsidian's backlinks show you what links where, but a note never goes stale; you'd hunt down the dependents by hand.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The reader is an agent, and it can write back</h3>
            <p>When Claude Code or Cursor is doing the work, it needs to both read your decisions and record new ones with structure. Via MCP, agents read the card graph and create or update cards. In Obsidian an agent can edit text, but it's still loose prose — no decision identity, no dependencies, nothing to track.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Decisions, not notes</h3>
            <p>A decision has an owner, a status, things it depends on, and things that depend on it. A markdown note has none of that — it's just text. Draftlize makes the decision the first-class unit so a project's reasoning becomes a graph you (and your agents) can query, not a folder you scroll.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Obsidian is where you keep what you've read. Draftlize is where your decisions stay alive for the agents building from them.
        </blockquote>
        <cite>They're not substitutes — one is your knowledge vault, the other is your decision substrate.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start free with <em>$5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Keep Obsidian for the notes you read. Add Draftlize for the product decisions your AI agents need to read, write, and keep in sync.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
